import React from 'react';
import ImageUpload from '../ImageUpload';
import HeroPreview from '../HeroPreview';
import { updateBlogPost } from '../../services/dataService';

const ImagesSection = ({ siteData, updateSiteData, setSiteData, showSaveStatus }) => {
    const handleAboutImageUpload = async (imageUrl, imagePath) => {
        try {
            const updatedAbout = {
                ...siteData.about,
                image: imageUrl,
                imagePath: imagePath
            };

            setSiteData(prev => ({
                ...prev,
                about: updatedAbout
            }));

            await updateSiteData('about', updatedAbout);
            showSaveStatus('About image updated successfully!');
        } catch (error) {
            console.error('Failed to update about image:', error);
            showSaveStatus('Failed to update about image', true);
        }
    };

    const handleBlogUpdate = async (postId, field, value) => {
        console.log('Debug - postId type:', typeof postId, 'value:', postId);
        console.log('Debug - field:', field, 'value:', value);

        try {
            const postToUpdate = siteData.blog.find(p => p.id === postId);
            console.log('Debug - found post:', postToUpdate);

            if (!postToUpdate) {
                console.error('Post not found with ID:', postId);
                showSaveStatus('Blog post not found', true);
                return;
            }

            const updatedPost = { ...postToUpdate, [field]: value };
            console.log('Debug - about to call updateBlogPost with:', postId, updatedPost);

            // Ensure postId is a string
            const postIdString = String(postId);
            await updateBlogPost(postIdString, updatedPost);

            // Update local state
            const updatedBlog = siteData.blog.map(post =>
                post.id === postId ? updatedPost : post
            );
            setSiteData(prev => ({ ...prev, blog: updatedBlog }));

        } catch (error) {
            console.error('Failed to update blog post image:', error);
            showSaveStatus('Failed to update blog image', true);
        }
    };

    return (
        <div className="space-y-12">
            <h3 className="text-xl font-semibold text-stone-800 mb-6">Website Images</h3>

            {/* Hero Background Image */}
            <div className="bg-white p-8 rounded-lg border border-stone-200">
                <h4 className="text-lg font-medium text-stone-800 mb-6">Hero Section</h4>
                <ImageUpload
                    currentImage={siteData.hero.backgroundImage}
                    onImageUpload={async (imageUrl, imagePath) => {
                        try {
                            const updatedHero = {
                                ...siteData.hero,
                                backgroundImage: imageUrl,
                                backgroundImagePath: imagePath
                            };

                            setSiteData(prev => ({
                                ...prev,
                                hero: updatedHero
                            }));

                            await updateSiteData('hero', updatedHero);

                            showSaveStatus('Hero background updated successfully!');
                        } catch (error) {
                            console.error('Failed to update hero background:', error);
                            showSaveStatus('Failed to update hero background', true);
                        }
                    }}
                    label="Hero Background Image"
                    folder="hero"
                    enableCrop={true}
                    previewComponent={
                        <HeroPreview
                            backgroundImage={siteData.hero.backgroundImage}
                            title={siteData.hero.title}
                            subtitle={siteData.hero.subtitle}
                            buttonText={siteData.hero.buttonText}
                        />
                    }
                />
            </div>

            {/* About Section Image */}
            <div className="bg-white p-8 rounded-lg border border-stone-200">
                <h4 className="text-lg font-medium text-stone-800 mb-6">About Section</h4>
                <ImageUpload
                    currentImage={siteData.about.image}
                    onImageUpload={handleAboutImageUpload}
                    label="About Section Image"
                    folder="about"
                    enableCrop={true}
                    aspectRatio={4 / 3}
                />
            </div>

            {/* Blog Featured Images */}
            <div className="bg-white p-8 rounded-lg border border-stone-200">
                <h4 className="text-lg font-medium text-stone-800 mb-6">Blog Post Images</h4>
                <p className="text-stone-600 mb-6">Upload featured images for individual blog posts in the Blog section.</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {siteData.blog.slice(0, 4).map((post) => (
                        <div key={post.id} className="border border-stone-200 rounded-lg p-6">
                            <h5 className="font-medium text-stone-800 mb-4 text-lg truncate">{post.title}</h5>
                            <ImageUpload
                                currentImage={post.featuredImage}
                                onImageUpload={async (imageUrl, imagePath) => {
                                    console.log('🖼️ Image upload completed:', { imageUrl, imagePath, postId: post.id });

                                    // Update both fields
                                    await handleBlogUpdate(post.id, 'featuredImage', imageUrl);
                                    await handleBlogUpdate(post.id, 'featuredImagePath', imagePath);

                                    showSaveStatus(`Image updated for "${post.title}"!`);
                                }}
                                label="Featured Image"
                                folder={`blog/${post.id}`}
                                enableCrop={true}
                                aspectRatio={16 / 9}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* General Website Images */}
            <div className="bg-white p-8 rounded-lg border border-stone-200">
                <h4 className="text-lg font-medium text-stone-800 mb-6">General Images</h4>
                <p className="text-stone-600 mb-8">Upload additional images for use throughout the website.</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                    <div className="space-y-2">
                        <ImageUpload
                            currentImage={siteData.general?.logo}
                            onImageUpload={(imageUrl, imagePath) => {
                                const updatedGeneral = {
                                    ...siteData.general,
                                    logo: imageUrl,
                                    logoPath: imagePath
                                };
                                updateSiteData('general', updatedGeneral);
                                showSaveStatus('Logo updated successfully!');
                            }}
                            label="Website Logo"
                            folder="general"
                            aspectRatio={2 / 1}
                        />
                    </div>

                    <div className="space-y-2">
                        <ImageUpload
                            currentImage={siteData.general?.officePhoto}
                            onImageUpload={(imageUrl, imagePath) => {
                                const updatedGeneral = {
                                    ...siteData.general,
                                    officePhoto: imageUrl,
                                    officePhotoPath: imagePath
                                };
                                updateSiteData('general', updatedGeneral);
                                showSaveStatus('Office photo updated successfully!');
                            }}
                            label="Office Photo"
                            folder="general"
                            aspectRatio={4 / 3}
                        />
                    </div>

                    <div className="space-y-2">
                        <ImageUpload
                            currentImage={siteData.general?.profilePhoto}
                            onImageUpload={(imageUrl, imagePath) => {
                                const updatedGeneral = {
                                    ...siteData.general,
                                    profilePhoto: imageUrl,
                                    profilePhotoPath: imagePath
                                };
                                updateSiteData('general', updatedGeneral);
                                showSaveStatus('Profile photo updated successfully!');
                            }}
                            label="Profile Photo"
                            folder="general"
                            aspectRatio={1 / 1}
                        />
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-8">
                <h4 className="text-amber-800 font-medium mb-3 text-lg">Image Upload Tips</h4>
                <ul className="text-amber-700 space-y-2">
                    <li>• Use high-quality images for best results</li>
                    <li>• Recommended sizes: Hero (1920x1080), About (800x600), Blog (600x400)</li>
                    <li>• Supported formats: JPEG, PNG, WebP</li>
                    <li>• Maximum file size: 5MB per image</li>
                    <li>• Images are automatically optimized and resized</li>
                    <li>• Use the crop tool to get perfect positioning</li>
                </ul>
            </div>
        </div>
    );
};

export default ImagesSection;